import React from 'react';
type Tome = {
  number: string;
  title: string;
  weeks: string;
  framework: string;
  promise: string;
  badge: string;
  contents: string[];
};
const tomes: Tome[] = [{
  number: '01',
  title: 'Start Strong',
  weeks: 'Weeks 1–7',
  framework: 'The Flex-Level Class Method',
  promise: 'Set up your room, your rules and the first steps every kid can dance.',
  badge: 'bg-sage',
  contents: ['Week 1: a first class that sets rules kids actually follow', 'The Freeze-Frame Reset: get the room back without raising your voice', 'Chassé, jazz square, ball change and pivot turns, broken down', '“First Four Eights”: the first full combo, in three levels', 'Step Dictionary: 10 foundation steps with counts and cues', 'Big groups: running these plans with 20+ kids']
}, {
  number: '02',
  title: 'Groove & Musicality',
  weeks: 'Weeks 8–14',
  framework: 'The Groove Dial',
  promise: 'Get your dancers moving with the music, not just near it.',
  badge: 'bg-smoke',
  contents: ['The Count-Clap-Move Ladder: teach rhythm in three rungs', 'Isolations that look sharp and stay kid-safe', 'Dancing on the “and” without losing the beat', 'BPM guide for warm-ups, combos and games with 6–12s', 'Clean-music checklist for age-appropriate tracks', 'Body percussion drills: rhythm practice with no music at all']
}, {
  number: '03',
  title: 'Travel, Turns & Jumps',
  weeks: 'Weeks 15–21',
  framework: 'The Floor Lanes System',
  promise: 'Across-the-floor classes that stay safe, organized and exciting.',
  badge: 'bg-coral',
  contents: ['Across-the-floor for 20 kids without chaos', 'Prep-Spot-Finish: the three-step way to teach any turn', 'Bend-Land-Hold: jumps that land soft and quiet', 'The run-run-leap kids ask for, with safe take-off and landing', 'Turn troubleshooting: 6 common problems and the fix for each', 'Travel classes in tight spaces']
}, {
  number: '04',
  title: 'Create & Perform',
  weeks: 'Weeks 22–28',
  framework: 'The Build-a-Move Formula',
  promise: 'Let your dancers make their own moves and work as a team.',
  badge: 'bg-lilac',
  contents: ['Improv that doesn’t turn into chaos', 'Every kid builds their own 8-count', 'The Stage Grid: 9 spots every kid can find', 'Four formations kids can find in seconds', 'Prompt Bank: 40 creative movement prompts', 'Formation Library: 12 formations with diagrams']
}, {
  number: '05',
  title: 'Showcase Build',
  weeks: 'Weeks 29–34',
  framework: 'The Six-Week Showcase Map',
  promise: 'A complete showcase routine taught, spaced and cleaned in six weeks.',
  badge: 'bg-gold-deep',
  contents: ['One full routine, section by section, in three levels', 'Clean-in-Three: counts, spacing, faces', 'Assigning spots fairly: rotation rules parents and kids accept', 'A parent showcase letter, ready to send', 'Pre-show nerves: a calm-down routine for the wings', 'Missed rehearsals: a catch-up plan that doesn’t stop the class']
}, {
  number: '06',
  title: 'Show Week & Season Finale',
  weeks: 'Weeks 35–40',
  framework: 'The Show-Day Run Sheet',
  promise: 'Run show week calmly, close the season well and plan the next one.',
  badge: 'bg-plum-mid',
  contents: ['Dress rehearsal and show-week classes', 'Every minute of show day on one page', '20 non-competitive awards every dancer can earn', 'Two sub plans anyone can teach', 'A bring-a-friend open class', 'The Encore Reset: plan next season in one hour']
}];
export function Tomes() {
  return <section aria-labelledby="tomes-heading" className="w-full bg-cream" data-id="element-297" data-mp="3r9uc3j9nnym3">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-298" data-mp="3vc8vq5l5f2ef">
        <div className="text-center" data-id="element-299" data-mp="3vae7oiekyu64">
          <p className="text-[11px] font-semibold uppercase tracking-[1.8px] text-gold-deep" data-id="element-300" data-mp="3c7cec8ahko8e">
            The complete season
          </p>
          <h2 id="tomes-heading" className="mt-3 font-display font-bold text-plum text-3xl sm:text-5xl" data-id="element-301" data-mp="37ac7hv5tbhg8">
            Six volumes. <span className="text-coral-deep" data-id="element-302" data-mp="3h78ic7j6ofny">Forty weeks.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-303" data-mp="3h544awrkm09y">
            Each volume is one stage of the season, from the first class to the
            final bow, and works on its own: its warm-ups, combos, games and
            worksheets are all inside it.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-304" data-mp="8osspombcgmk">
          {tomes.map(tome => <article key={tome.number} className="flex flex-col rounded-3xl border border-cream-dark bg-white p-6 shadow-card" data-id="element-305" data-mp="2aiibaen0acpv">
              <div className="flex items-center gap-2.5" data-id="element-306" data-mp="25l9x0p4zastw">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full font-display text-base font-bold text-white ${tome.badge}`} data-id="element-307" data-mp="l14s5g0e16n2">
                  {tome.number}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-ink-muted" data-id="element-308" data-mp="bbbgyg45qtjc">
                  Volume {tome.number} · {tome.weeks}
                </span>
              </div>
              <h3 className="mt-3 font-display text-2xl font-bold leading-snug text-plum" data-id="element-309" data-mp="29lqpdm0i0n5">
                {tome.title}
              </h3>
              <p className="mt-1 text-[12px] font-semibold uppercase tracking-wider text-coral-deep" data-id="element-310" data-mp="3oid81ubk1wf5">
                {tome.framework}
              </p>
              <p className="mt-2 text-sm text-ink-soft" data-id="element-311" data-mp="3thgcbcjpnym2">{tome.promise}</p>
              <ul className="mt-4 grid gap-2 border-t border-cream-dark pt-4" data-id="element-312" data-mp="1mrrziib0dqaf">
                {tome.contents.map(item => <li key={item} className="flex gap-2 text-[13px] text-ink" data-id="element-313" data-mp="1rp1uedh62u5n">
                    <span className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-gold" data-id="element-314" data-mp="1q9fn20p060d5" />
                    {item}
                  </li>)}
              </ul>
            </article>)}
        </div>
      </div>
    </section>;
}